import { useContext } from 'react';
import { useRouter } from 'next/router';
import useTagIds from '@discover-data/stores/hooks/tagIds/useTagIds';
import type { AccordionTriggerProps } from '@discover-ui/accordion';
import CheckboxGroup from '@discover-ui/checkboxGroup';
import { cuisineOptions, cuisineHref } from '@/lib/cityCuisine';
import SilverstripeContext from '@/components/context/Silverstripe';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

import FilterAccordionItem from '../filterAccordionItem';
import useExpandedFilterOptions, { COLLAPSED_FILTER_LIMIT } from '../useExpandedFilterOptions';

export type TagsAccordionItemProps = {
  title: string;
  tags: {
    category: string | null;
    title: string | null;
    id: number | null;
    disabled?: boolean;
  }[];
  triggerProps?: AccordionTriggerProps;
  renderCollapsedOptions?: boolean;
};

export default function TagsAccordionItem({
  title,
  tags,
  triggerProps,
  renderCollapsedOptions = true,
}: TagsAccordionItemProps) {
  const [tagIds, setTagIds] = useTagIds(tags);
  const { asPath } = useRouter();
  const { page } = useContext(SilverstripeContext);
  // Only cuisines with a page on this location get a crawlable href.
  const cuisineHrefs = new Map(
    (page?.className === 'City' ? cuisineOptions(page.cuisineRoutes || []) : []).map((cuisine) => [
      String(cuisine.id),
      cuisineHref(asPath, cuisine.slug),
    ])
  );
  const hasAdditionalSelection = tags
    .slice(COLLAPSED_FILTER_LIMIT)
    .some(({ id }) => id !== null && tagIds.includes(id));
  const { expanded, toggleExpanded } = useExpandedFilterOptions(
    hasAdditionalSelection,
    !renderCollapsedOptions && hasAdditionalSelection
  );

  const { track } = useAnalytics();

  const selectedCount = tagIds.filter((selectedId) =>
    tags.some(({ id }) => id === selectedId)
  ).length;

  const tagOptions = tags.map((tag) => ({
    value: `${tag.id}`,
    label: `${tag.title}`,
    disabled: tag.disabled,
  }));

  return (
    <FilterAccordionItem
      value={title}
      title={title}
      selectedCount={selectedCount}
      onClear={() => {
        const nonCategoryTagIds = tagIds.filter((tagId) => tags.every(({ id }) => id !== tagId));
        track('button_clicked', {
          button_copy: `CLEAR (${selectedCount})`,
          button_type: 'selection',
        });
        setTagIds(nonCategoryTagIds);
      }}
      expanded={expanded}
      onToggleExpanded={toggleExpanded}
      hasMore={tags.length > COLLAPSED_FILTER_LIMIT}
      triggerProps={triggerProps}>
      {[
        tagOptions.slice(0, COLLAPSED_FILTER_LIMIT),
        ...(renderCollapsedOptions || expanded ? [tagOptions.slice(COLLAPSED_FILTER_LIMIT)] : []),
      ].map((options, index) => (
        <div key={index} hidden={index === 1 && !expanded}>
          <CheckboxGroup
            options={options}
            values={tagIds.map((id) => `${id}`)}
            setValues={(newValues) => setTagIds(newValues.map((value) => parseInt(value)))}
            renderLabel={(option, toggle) => {
              const href = cuisineHrefs.get(option.value);
              if (!href || option.disabled) return null;
              return (
                <a
                  href={href}
                  className="flex-1 py-1 pr-2 text-sm hover:underline"
                  onClick={(event) => {
                    // A modified click opens the cuisine page; a plain click is the checkbox toggle.
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    )
                      return;
                    event.preventDefault();
                    toggle();
                  }}>
                  {option.label}
                </a>
              );
            }}
          />
        </div>
      ))}
    </FilterAccordionItem>
  );
}
